// oxlint-disable-next-line no-restricted-imports
import { ActionIcon, Group, HoverCard, Stack, Tooltip as MantineTooltip } from "@mantine/core";
import { Fragment } from "react";

import { colors } from "#app/theme/v2/colors";
import { fontSizes } from "#app/theme/v2/fontSizes";
import { fontWeights } from "#app/theme/v2/fontWeights";
import { radius } from "#app/theme/v2/radius";
import { shadows } from "#app/theme/v2/shadows";
import { spacing } from "#app/theme/v2/spacing";
import { Icon, IconName } from "#shared/icons/Icon";

import { Button } from "../Button/Button";
import { Divider } from "../Divider/Divider";
import { Text } from "../Typographies/Text";

type TooltipPosition = "top" | "bottom" | "left" | "right";

const TOOLTIP_BG = colors.bg.elevation.tooltip;
const TOOLTIP_SHADOW = shadows.elevation4;
const TOOLTIP_CLOSE_DELAY = 200;
const TOOLTIP_MAX_WIDTH = 320;
const TOOLTIP_ICON_SIZE = 16;
const TOOLTIP_CLOSE_ICON_SIZE = 14;

const HOVER_CARD_STYLES = {
  dropdown: {
    backgroundColor: TOOLTIP_BG,
    borderRadius: radius.md,
    border: "none",
    boxShadow: TOOLTIP_SHADOW,
    padding: spacing[3],
    maxWidth: TOOLTIP_MAX_WIDTH,
  } as React.CSSProperties,
};

type TooltipItem = {
  label: React.ReactNode;
  icon?: IconName;
  iconColor?: string;
  buttonLabel?: string;
  onButtonClick?: () => void;
};

type TooltipProps = {
  children: React.ReactElement;
  position?: TooltipPosition;
} & (
  | {
      type?: "small";
      label: React.ReactNode;
      disabled?: boolean;
      // false skips the wrapping span for children that already forward a ref, avoiding broken flex layouts
      wrap?: boolean;
    }
  | ({ type: "large"; onClose?: () => void; closeLabel?: string } & TooltipItem)
  | {
      type: "list";
      items: TooltipItem[];
      onClose?: () => void;
      closeLabel?: string;
    }
);

const TooltipItemContent = ({
  label,
  icon,
  iconColor,
  buttonLabel,
  onButtonClick,
}: TooltipItem) => (
  <Group wrap="nowrap" align="flex-start" gap={spacing[2]}>
    {icon && (
      <Icon
        name={icon}
        size={TOOLTIP_ICON_SIZE}
        color={iconColor ?? colors.icon.primaryInverse}
        style={{ flexShrink: 0, marginTop: spacing[0.5] }}
      />
    )}
    <Stack align="flex-start" gap={spacing[2]} style={{ flex: "1 0 0", minWidth: 0 }}>
      <Text size="small" color={colors.text.primaryInverse}>
        {label}
      </Text>
      {buttonLabel && (
        <Button variant="tertiary" size="sm" onClick={onButtonClick}>
          {buttonLabel}
        </Button>
      )}
    </Stack>
  </Group>
);

const Tooltip = (props: TooltipProps) => {
  const { children, position } = props;

  if (props.type !== "large" && props.type !== "list") {
    const { label, disabled, wrap = true } = props;

    return (
      <MantineTooltip
        label={label}
        position={position ?? "top"}
        disabled={disabled}
        withArrow={false}
        radius={radius.xs}
        styles={{
          tooltip: {
            backgroundColor: TOOLTIP_BG,
            color: colors.text.primaryInverse,
            padding: `${spacing[1.5]} ${spacing[2.5]}`,
            boxShadow: TOOLTIP_SHADOW,
            fontSize: fontSizes[12],
            fontWeight: fontWeights.medium,
            lineHeight: fontSizes[16],
            letterSpacing: "0.096px",
            whiteSpace: "nowrap",
          },
        }}
      >
        {wrap ? <span style={{ display: "inline-flex" }}>{children}</span> : children}
      </MantineTooltip>
    );
  }

  const { onClose, closeLabel = "Close" } = props;
  const items = props.type === "list" ? props.items : [props];

  return (
    <HoverCard
      position={position ?? "bottom"}
      withArrow={false}
      closeDelay={TOOLTIP_CLOSE_DELAY}
      styles={HOVER_CARD_STYLES}
    >
      <HoverCard.Target>
        <span style={{ display: "inline-flex" }}>{children}</span>
      </HoverCard.Target>
      <HoverCard.Dropdown>
        <Group wrap="nowrap" align="flex-start" gap={spacing[2]}>
          <Stack gap={spacing[3]} style={{ flex: "1 0 0", minWidth: 0 }}>
            {items.map((item, index) => (
              <Fragment key={index}>
                {index > 0 && <Divider color={colors.icon.secondaryInverse} />}
                <TooltipItemContent {...item} />
              </Fragment>
            ))}
          </Stack>
          {onClose && (
            <ActionIcon
              variant="transparent"
              size={spacing[5]}
              aria-label={closeLabel}
              onClick={onClose}
              style={
                {
                  "--ai-color": colors.icon.tertiary,
                  "--ai-hover-color": colors.icon.primaryInverse,
                } as React.CSSProperties
              }
            >
              <Icon name="x" size={TOOLTIP_CLOSE_ICON_SIZE} />
            </ActionIcon>
          )}
        </Group>
      </HoverCard.Dropdown>
    </HoverCard>
  );
};

export { Tooltip };
